import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, HostListener, Inject, inject, OnDestroy, OnInit, Optional, ViewChild, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, catchError, debounceTime, distinctUntilChanged, forkJoin, of, switchMap, takeUntil, tap } from 'rxjs';
import { NavigationConfig } from '../../models/navigation-config';
import { AuthService } from '../../services/auth/auth.service';
import { BaseViewConfigService } from '../../services/base-view-config/base-view-config.service';
import { ClerkCategory, ClerkProduct, ClerkSearchService } from '../../services/clerk-search/clerk-search.service';
import { ClerkGlobalSearchResultsComponent, ClerkSearchResultsData } from '../clerk-global-search-results/clerk-global-search-results.component';

@Component({
  selector: 'lib-clerk-global-search',
  templateUrl: './clerk-global-search.component.html',
  styleUrls: ['./clerk-global-search.component.scss'],
  standalone: false
})
export class ClerkGlobalSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('resultsHost', { read: ViewContainerRef }) resultsHost!: ViewContainerRef;

  searchInputValue = '';
  isLoading = false;
  showResults = false;
  showNoResults = false;

  private products: ClerkProduct[] = [];
  private categories: ClerkCategory[] = [];
  private suggestions: string[] = [];
  private totalCount = 0;

  private readonly renderData = new Subject<ClerkSearchResultsData>();
  private resultsComponentRef!: ComponentRef<ClerkGlobalSearchResultsComponent>;
  readonly searchTerms = new BehaviorSubject<string>('');
  private readonly unsubscribe = new Subject<void>();

  public maxResultShow = 5;
  public maxCategoriesShow = 6;
  public maxSuggestionsShow = 3;
  public searchPageRoute = '/search';
  public searchQueryParam = 'query';
  public inputSearchIcon = 'fa-light fa-magnifying-glass';
  public inputClearIcon = 'fa-light fa-xmark';
  public inputLoadingIcon = 'fa-regular fa-spinner fa-spin';
  public searchInputPlaceholder = 'SEARCH_PRODUCTS';

  private clerkSearchService = inject(ClerkSearchService);
  private baseViewConfigService = inject(BaseViewConfigService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  constructor(@Optional() @Inject('environment') private environment: NavigationConfig) {}

  get apiKey(): string {
    return this.environment?.clerkApiKey || '';
  }

  @HostListener('window:click', ['$event.target'])
  onWindowClick(target: Element): void {
    if (!target.closest('.clerk-search-wrapper')) {
      this.closeResults();
    }
  }

  ngOnInit(): void {
    let emailLogged = false;
    this.authService.getCurrentUser().pipe(takeUntil(this.unsubscribe)).subscribe(user => {
      if (user?.email && !emailLogged && this.apiKey) {
        emailLogged = true;
        this.clerkSearchService.logEmail(this.apiKey).subscribe();
      }
    });

    this.searchTerms.pipe(
      takeUntil(this.unsubscribe),
      tap(() => { this.isLoading = true; }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || !this.apiKey) {
          return of(null);
        }
        return forkJoin({
          predictive: this.clerkSearchService.predictive(query, this.apiKey, this.maxResultShow).pipe(catchError(() => of(null))),
          categories: this.clerkSearchService.categories(query, this.apiKey, this.maxCategoriesShow).pipe(catchError(() => of(null))),
          suggestions: this.clerkSearchService.suggestions(query, this.apiKey, this.maxSuggestionsShow).pipe(catchError(() => of(null)))
        });
      })
    ).subscribe(response => {
      this.isLoading = false;
      if (!response) {
        this.products = [];
        this.categories = [];
        this.suggestions = [];
        this.totalCount = 0;
        this.showNoResults = !!this.searchInputValue;
        this.showResults = false;
        this.pushRenderData();
        return;
      }
      this.products = response.predictive?.product_data || [];
      this.totalCount = response.predictive?.hits ?? this.products.length;
      this.categories = response.categories?.categories || [];
      this.suggestions = response.suggestions?.result || [];
      this.showNoResults = this.products.length === 0 && this.categories.length === 0 && this.suggestions.length === 0;
      this.showResults = true;
      this.pushRenderData();
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    if (this.resultsHost) {
      this.resultsComponentRef = this.resultsHost.createComponent(
        this.baseViewConfigService.getClerkSearchResultsComponent()
      );
      const instance = this.resultsComponentRef.instance;
      this.resultsComponentRef.setInput('renderData', this.renderData);
      instance.closeResultsEmitter.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.closeResults());
      instance.navigateToSearchEmitter.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.navigateToSearch());
      instance.navigateToSuggestionEmitter.pipe(takeUntil(this.unsubscribe)).subscribe(s => this.navigateToSuggestion(s));
      instance.productClickEmitter.pipe(takeUntil(this.unsubscribe)).subscribe(id => {
        if (this.apiKey) {
          this.clerkSearchService.logClick(this.apiKey, id, 'Search - Predictive', 'search/predictive').subscribe();
        }
      });
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private pushRenderData(): void {
    this.renderData.next({
      products: this.products,
      categories: this.categories,
      suggestions: this.suggestions,
      totalCount: this.totalCount,
      maxResultShow: this.maxResultShow,
      showNoResults: this.showNoResults,
      searchValue: this.searchInputValue
    });
  }

  onInputChange(value: string): void {
    if (!value) {
      this.clearResults();
      return;
    }
    this.searchTerms.next(value);
  }

  onFocus(): void {
    if (this.searchInputValue && (this.products.length > 0 || this.categories.length > 0 || this.suggestions.length > 0)) {
      this.showResults = true;
    }
  }

  navigateToSearch(): void {
    if (!this.searchInputValue) {
      return;
    }
    const params: { [k: string]: string } = {};
    params[this.searchQueryParam] = this.searchInputValue;
    this.router.navigate([this.searchPageRoute], { queryParams: params });
    this.closeResults();
  }

  navigateToSuggestion(suggestion: string): void {
    this.searchInputValue = suggestion;
    const params: { [k: string]: string } = {};
    params[this.searchQueryParam] = suggestion;
    this.router.navigate([this.searchPageRoute], { queryParams: params });
    this.closeResults();
  }

  closeResults(): void {
    this.showResults = false;
  }

  clearResults(): void {
    this.products = [];
    this.categories = [];
    this.suggestions = [];
    this.totalCount = 0;
    this.showResults = false;
    this.showNoResults = false;
    this.searchInputValue = '';
    this.searchTerms.next('');
    this.pushRenderData();
  }

  getInputIcon(): string {
    if (this.isLoading) return this.inputLoadingIcon;
    if (this.products.length > 0 || this.searchInputValue) return this.inputClearIcon;
    return this.inputSearchIcon;
  }

  onIconButtonClick(): void {
    if (this.isLoading) return;
    if (this.products.length > 0 || this.searchInputValue) {
      this.clearResults();
    } else {
      this.navigateToSearch();
    }
  }
}
